/**
 * OpenAI-specific utility functions and constants related to the realtime API.
 * For now, we define the relevant constants that we actually use since it's
 * fairly minimal, but we may want to pull in typedefs from a library in the future.
 *
 * Docs:
 * https://platform.openai.com/docs/api-reference/realtime-client-events
 * https://platform.openai.com/docs/api-reference/realtime-server-events
 */

export interface FunctionCallDoneEvent {
  type: 'conversation.item.done';
  item: {
    type: 'function_call';
    name: string;
    call_id: string;
    arguments: string;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFunctionCallDoneEvent(event: any): event is FunctionCallDoneEvent {
  return event.type === 'conversation.item.done' && event.item.type === 'function_call';
}

/**
 * Create an event to append a tool response to the conversation.
 */
export function createToolResponseEvent(callId: string, output: string): unknown {
  return {
    type: 'conversation.item.create',
    item: {
      type: 'function_call_output',
      call_id: callId,
      output,
    },
  };
}

/**
 * Create an event to start model inference. This must be triggered directly for
 * manually-posted events.
 */
export function createTriggerInferenceEvent(): unknown {
  return {
    type: 'response.create',
  };
}

// Realtime transcript events arrive independently and are correlated by OpenAI item ID.

export interface ConversationItemCreatedEvent {
  type: 'conversation.item.created';
  // Only user items need asynchronous transcription and its never-drop timeout.
  item: {id: string; role?: 'user' | 'assistant'};
  previous_item_id: string | null;
}

/** Captures turn ordering before its transcript completes. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isConversationItemCreatedEvent(event: any): event is ConversationItemCreatedEvent {
  return event.type === 'conversation.item.created';
}

export interface AssistantOutputItemAddedEvent {
  type: 'response.output_item.added';
  response_id: string;
  item: {
    id: string;
    type: 'message';
    role: 'assistant';
  };
}

/** Matches the first event that identifies an assistant message generated by a response. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAssistantOutputItemAddedEvent(event: any): event is AssistantOutputItemAddedEvent {
  return (
    event.type === 'response.output_item.added' &&
    event.item?.type === 'message' &&
    event.item.role === 'assistant'
  );
}

export interface ResponseDoneEvent {
  type: 'response.done';
  response: {
    id: string;
    status: 'completed' | 'cancelled' | 'failed' | 'incomplete';
  };
}

/** Matches the terminal event for a generated response. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isResponseDoneEvent(event: any): event is ResponseDoneEvent {
  return event.type === 'response.done' && typeof event.response?.id === 'string';
}

export interface InputAudioBufferSpeechStartedEvent {
  type: 'input_audio_buffer.speech_started';
  item_id: string;
}

/** Matches server VAD speech start, before the user conversation item is committed. */
export function isInputAudioBufferSpeechStartedEvent(
  event: unknown
): event is InputAudioBufferSpeechStartedEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === 'input_audio_buffer.speech_started' &&
    'item_id' in event &&
    typeof event.item_id === 'string'
  );
}

export interface InputAudioBufferClearedEvent {
  type: 'input_audio_buffer.cleared';
}

/** Matches cancellation of uncommitted input audio. */
export function isInputAudioBufferClearedEvent(event: unknown): event is InputAudioBufferClearedEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === 'input_audio_buffer.cleared'
  );
}

export interface AssistantTranscriptDoneEvent {
  type: 'response.output_audio_transcript.done';
  item_id: string;
  transcript: string;
}

/** Matches the completed assistant transcript emitted by the Realtime model. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAssistantTranscriptDoneEvent(event: any): event is AssistantTranscriptDoneEvent {
  return event.type === 'response.output_audio_transcript.done';
}

export interface UserTranscriptionCompletedEvent {
  type: 'conversation.item.input_audio_transcription.completed';
  item_id: string;
  transcript: string;
}

/** Matches asynchronous user transcription, correlated by item ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isUserTranscriptionCompletedEvent(event: any): event is UserTranscriptionCompletedEvent {
  return event.type === 'conversation.item.input_audio_transcription.completed';
}

export interface UserTranscriptionFailedEvent {
  type: 'conversation.item.input_audio_transcription.failed';
  item_id: string;
}

/** Matches transcription failure so the caller can persist a terminal fallback. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isUserTranscriptionFailedEvent(event: any): event is UserTranscriptionFailedEvent {
  return event.type === 'conversation.item.input_audio_transcription.failed';
}
